import { CatalogueContributions } from './catalogue-contributions/workspace';
import { CommunityReports } from './community-reports/community-reports';
import { COMMUNITY_REPORT_COPY } from './community-reports/copy';
import { CatalogueSyncPanel } from './catalogue-sync-panel';
import { ProfilePhotoModeration } from './profile-photo-moderation';
import { useAuth } from '@workos-inc/authkit-react';
import { BoardWorkspace } from './board/board-workspace';
import { AchievementsOverview } from './achievements-overview';
import { c as boardCopy } from './board/board-ui';
import { UnifiedQuoteQueue, quoteModerationCopy } from './unified-quote-queue';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminApiError,
  type AdminReviewFilters,
  assertAdminAccess,
  type CataloguePipelineIssueCode,
  type CataloguePipelineOperations,
  decideReview,
  getCataloguePipelineOperations,
  getOpenLibraryRatingOperations,
  getPendingReviews,
  getPendingReviewers,
  type OpenLibraryRatingOperations,
  type PendingReview,
  type PendingReviewQueue,
  type PendingQuote,
  type PendingQuoteQueue,
  type PendingReviewerOption,
  type ReviewCheckStatus,
  type ReviewContentCheck,
  type ReviewContentSignalCode,
  type ReviewDecision,
  syncProfile,
} from './admin-api.ts';
import {
  ADMIN_ACCESS_DENIED_MESSAGE,
  ADMIN_SSO_PROVIDERS,
  type AdminSsoProvider,
  adminLoginError,
  adminLogoutReturnUrl,
  adminSsoAuthorizationUrl,
  completeRejectedSession,
  isAdminSsoAuthenticationMethod,
} from './auth-routes.ts';
import { type WorkspaceView, workspacePath, workspaceViewFromPath } from './workspace-routes.ts';

type QueueState =
  | { kind: 'checking' }
  | { kind: 'loading' }
  | { kind: 'ready'; queue: PendingReviewQueue }
  | { kind: 'leaving' }
  | { kind: 'error'; message: string };

type OperationsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; operations: OpenLibraryRatingOperations }
  | { kind: 'error'; message: string };
type CatalogueState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; operations: CataloguePipelineOperations }
  | { kind: 'error'; message: string };
export type { WorkspaceView } from './workspace-routes.ts';

interface BusyDecision {
  decision: ReviewDecision;
  reviewId: string;
}

interface DecisionError {
  message: string;
  reviewId: string;
}

type ModeratedReviews = Record<string, ReviewDecision>;
const emptyReviewFilters: AdminReviewFilters = {
  checkStatus: null,
  reviewerIds: [],
};

export function mergePendingReviewPage(
  current: PendingReviewQueue,
  page: PendingReviewQueue,
): PendingReviewQueue {
  const existingIds = new Set(current.reviews.map((review) => review.id));
  return {
    nextCursor: page.nextCursor,
    reviews: [...current.reviews, ...page.reviews.filter((review) => !existingIds.has(review.id))],
    total: Math.max(current.total, page.total),
  };
}

export function mergePendingQuotePage(
  current: PendingQuoteQueue,
  page: PendingQuoteQueue,
): PendingQuoteQueue {
  const existingIds = new Set(current.quotes.map((quote) => quote.id));
  return {
    nextCursor: page.nextCursor,
    quotes: [...current.quotes, ...page.quotes.filter((quote) => !existingIds.has(quote.id))],
    total: Math.max(current.total, page.total),
  };
}

const focusRing =
  'focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-thread';
const buttonBase = `admin-button w-full sm:w-auto ${focusRing}`;
const primaryButton = `admin-button-primary w-full sm:w-auto ${focusRing}`;
const eyebrow = 'admin-eyebrow';
const headingOne =
  'm-0 font-serif text-[clamp(2rem,4vw,2.75rem)] font-normal leading-[1.12] tracking-[-0.04em] text-balance';
const stateCard = 'admin-panel w-full max-w-[660px] p-[clamp(1.875rem,6vw,3.25rem)]';
const inlineError = 'mt-4 text-sm leading-[1.55] text-warning';
const maxModeratorMessageLength = 1_000;

function normalizeReviewerSearch(value: string): string {
  return value.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
}

export function filterReviewerOptions(
  reviewerOptions: PendingReviewerOption[],
  query: string,
): PendingReviewerOption[] {
  const normalizedQuery = normalizeReviewerSearch(query.trim());
  if (normalizedQuery.length === 0) {
    return reviewerOptions;
  }
  return reviewerOptions.filter((option) =>
    normalizeReviewerSearch(option.name ?? 'Unnamed reader').includes(normalizedQuery),
  );
}

export function App() {
  const workspaceRef = useRef<HTMLElement>(null);
  const { authenticationMethod, getAccessToken, getSignInUrl, isLoading, signOut, user } =
    useAuth();
  const [queueState, setQueueState] = useState<QueueState>({
    kind: 'checking',
  });
  const [activeView, setActiveView] = useState<WorkspaceView>(() =>
    workspaceViewFromPath(window.location.pathname),
  );
  const [operationsState, setOperationsState] = useState<OperationsState>({
    kind: 'idle',
  });
  const [catalogueState, setCatalogueState] = useState<CatalogueState>({
    kind: 'idle',
  });
  const [busyDecision, setBusyDecision] = useState<BusyDecision | null>(null);
  const [decisionError, setDecisionError] = useState<DecisionError | null>(null);
  const [moderatedReviews, setModeratedReviews] = useState<ModeratedReviews>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [reviewFilters, setReviewFilters] = useState<AdminReviewFilters>(emptyReviewFilters);
  const [reviewerOptions, setReviewerOptions] = useState<PendingReviewerOption[]>([]);

  const [signInError, setSignInError] = useState<string | null>(() =>
    adminLoginError(window.location.href),
  );
  const [signInProvider, setSignInProvider] = useState<AdminSsoProvider | null>(null);
  const accessRejectionStarted = useRef(false);
  const accessConfirmed = useRef(false);
  const loadingMoreStarted = useRef(false);
  const nextCursor = useRef<string | null>(null);
  const filterRequestId = useRef(0);
  const displayName = useMemo(
    () =>
      [user?.firstName, user?.lastName]
        .filter((part): part is string => Boolean(part?.trim()))
        .join(' ')
        .trim(),
    [user?.firstName, user?.lastName],
  );
  const signOutOfWorkspace = useCallback(() => {
    void signOut({
      returnTo: adminLogoutReturnUrl(window.location.origin),
    });
  }, [signOut]);

  const rejectAccess = useCallback(() => {
    if (accessRejectionStarted.current) {
      return;
    }
    accessRejectionStarted.current = true;
    setQueueState({ kind: 'leaving' });
    void completeRejectedSession(signOut, window.location.origin, (url) =>
      window.location.replace(url),
    );
  }, [signOut]);

  const loadQueue = useCallback(async () => {
    if (!user) {
      return;
    }
    if (!isAdminSsoAuthenticationMethod(authenticationMethod)) {
      rejectAccess();
      return;
    }
    setQueueState({
      kind: accessConfirmed.current ? 'loading' : 'checking',
    });
    try {
      await assertAdminAccess(getAccessToken);
      accessConfirmed.current = true;
      setQueueState({ kind: 'loading' });
      if (displayName) {
        await syncProfile(getAccessToken, displayName, user.profilePictureUrl ?? null);
      }
      const [queue, reviewers] = await Promise.all([
        getPendingReviews(getAccessToken, emptyReviewFilters),
        getPendingReviewers(getAccessToken),
      ]);
      nextCursor.current = queue.nextCursor;
      setReviewFilters(emptyReviewFilters);
      setReviewerOptions(reviewers);
      setModeratedReviews({});
      setLoadMoreError(null);
      setFilterError(null);
      setQueueState({ kind: 'ready', queue });
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) {
        rejectAccess();
        return;
      }
      setQueueState({
        kind: 'error',
        message: messageForError(cause),
      });
    }
  }, [authenticationMethod, displayName, getAccessToken, rejectAccess, user]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const restoreRoute = () => {
      setActiveView(workspaceViewFromPath(window.location.pathname));
      workspaceRef.current?.scrollTo({ top: 0 });
    };
    window.addEventListener('popstate', restoreRoute);
    return () => window.removeEventListener('popstate', restoreRoute);
  }, []);

  useEffect(() => {
    if (user && !isLoading && window.location.pathname === '/login') {
      window.history.replaceState({}, '', workspacePath(activeView));
    }
  }, [activeView, isLoading, user]);

  const loadOperations = useCallback(async () => {
    setOperationsState({ kind: 'loading' });
    try {
      const operations = await getOpenLibraryRatingOperations(getAccessToken);
      setOperationsState({ kind: 'ready', operations });
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) {
        rejectAccess();
        return;
      }
      setOperationsState({
        kind: 'error',
        message: messageForError(cause),
      });
    }
  }, [getAccessToken, rejectAccess]);

  const loadCatalogue = useCallback(async () => {
    setCatalogueState({ kind: 'loading' });
    try {
      const operations = await getCataloguePipelineOperations(getAccessToken);
      setCatalogueState({ kind: 'ready', operations });
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) {
        rejectAccess();
        return;
      }
      setCatalogueState({
        kind: 'error',
        message: messageForError(cause),
      });
    }
  }, [getAccessToken, rejectAccess]);

  const selectWorkspaceView = useCallback((view: WorkspaceView) => {
    const path = workspacePath(view);
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    setActiveView(view);
  }, []);

  useEffect(() => {
    if (!user || queueState.kind === 'checking' || queueState.kind === 'leaving') {
      return;
    }
    if (activeView === 'operations' && operationsState.kind === 'idle') {
      void loadOperations();
    }
    if (activeView === 'catalogue' && catalogueState.kind === 'idle') {
      void loadCatalogue();
    }
  }, [
    activeView,
    catalogueState.kind,
    loadCatalogue,
    loadOperations,
    operationsState.kind,
    queueState.kind,
    user,
  ]);

  const loadMore = useCallback(async () => {
    const cursor = nextCursor.current;
    const requestGeneration = filterRequestId.current;
    if (!cursor || loadingMoreStarted.current) {
      return;
    }
    loadingMoreStarted.current = true;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await getPendingReviews(getAccessToken, reviewFilters, cursor);
      if (filterRequestId.current !== requestGeneration) {
        return;
      }
      nextCursor.current = page.nextCursor;
      setQueueState((current) => {
        if (current.kind !== 'ready') {
          return current;
        }
        return {
          kind: 'ready',
          queue: mergePendingReviewPage(current.queue, page),
        };
      });
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) {
        rejectAccess();
        return;
      }
      setLoadMoreError(messageForError(cause));
    } finally {
      loadingMoreStarted.current = false;
      setIsLoadingMore(false);
    }
  }, [getAccessToken, rejectAccess, reviewFilters]);

  const applyReviewFilters = useCallback(
    async (filters: AdminReviewFilters) => {
      const previousFilters = reviewFilters;
      const requestId = filterRequestId.current + 1;
      filterRequestId.current = requestId;
      setReviewFilters(filters);
      setIsFiltering(true);
      setFilterError(null);
      setLoadMoreError(null);
      try {
        const queue = await getPendingReviews(getAccessToken, filters);
        if (filterRequestId.current !== requestId) {
          return;
        }
        nextCursor.current = queue.nextCursor;
        setModeratedReviews({});
        setQueueState({ kind: 'ready', queue });
      } catch (cause) {
        if (cause instanceof AdminApiError && cause.status === 403) {
          rejectAccess();
          return;
        }
        if (filterRequestId.current === requestId) {
          setReviewFilters(previousFilters);
          setFilterError(messageForError(cause));
        }
      } finally {
        if (filterRequestId.current === requestId) {
          setIsFiltering(false);
        }
      }
    },
    [getAccessToken, rejectAccess, reviewFilters],
  );

  async function beginSso(provider: AdminSsoProvider) {
    setSignInError(null);
    setSignInProvider(provider);
    try {
      const authKitUrl = await getSignInUrl({
        state: { returnTo: window.location.pathname },
      });
      window.location.assign(adminSsoAuthorizationUrl(authKitUrl, provider));
    } catch {
      setSignInError('We could not start SSO. Please try again.');
      setSignInProvider(null);
    }
  }

  async function moderate(
    review: PendingReview,
    nextDecision: ReviewDecision,
    moderatorMessage: string | null = null,
  ) {
    if (
      queueState.kind !== 'ready' ||
      busyDecision !== null ||
      !queueState.queue.reviews.some((candidate) => candidate.id === review.id)
    ) {
      return;
    }
    setBusyDecision({ decision: nextDecision, reviewId: review.id });
    setDecisionError(null);
    try {
      await decideReview(getAccessToken, review, nextDecision, moderatorMessage);
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) {
        rejectAccess();
        return;
      }
      setDecisionError({
        message: messageForError(cause),
        reviewId: review.id,
      });
      setBusyDecision(null);
      return;
    }

    setModeratedReviews((current) => ({
      ...current,
      [review.id]: nextDecision,
    }));
    setBusyDecision(null);
  }

  if (isLoading) {
    return (
      <AuthFrame>
        <CenteredState title="Preparing your workspace…" />
      </AuthFrame>
    );
  }
  const accessRejectionInProgress = queueState.kind === 'leaving';
  if (!user || accessRejectionInProgress) {
    const loginError = accessRejectionInProgress ? ADMIN_ACCESS_DENIED_MESSAGE : signInError;
    return (
      <AuthFrame>
        <section
          className={`${stateCard} admin-auth-card max-w-[520px] text-center`}
          aria-live="polite"
        >
          <img
            className="mx-auto mb-[38px] block h-auto w-[154px]"
            src="/assets/belore-wordmark.svg"
            alt="Belore"
          />
          <p className={`${eyebrow} mb-3`}>Private access</p>
          <h1 className={`${headingOne} mb-[18px]`}>Welcome back</h1>
          <p className="mx-auto mb-[30px] max-w-[42ch] leading-[1.65] text-ink-soft text-pretty">
            Continue with your approved identity.
          </p>
          <div className="grid gap-3">
            <button
              className={`admin-button grid min-h-[50px] w-full grid-cols-[1.5rem_1fr_1.5rem] ${focusRing}`}
              disabled={signInProvider !== null || accessRejectionInProgress}
              onClick={() => void beginSso(ADMIN_SSO_PROVIDERS.google)}
              type="button"
            >
              <GoogleIcon />
              <span>
                {signInProvider === ADMIN_SSO_PROVIDERS.google
                  ? 'Opening Google…'
                  : 'Continue with Google'}
              </span>
              <span aria-hidden="true" />
            </button>
            <button
              className={`admin-button-primary grid min-h-[50px] w-full grid-cols-[1.5rem_1fr_1.5rem] ${focusRing}`}
              disabled={signInProvider !== null || accessRejectionInProgress}
              onClick={() => void beginSso(ADMIN_SSO_PROVIDERS.apple)}
              type="button"
            >
              <AppleIcon />
              <span>
                {signInProvider === ADMIN_SSO_PROVIDERS.apple
                  ? 'Opening Apple…'
                  : 'Continue with Apple'}
              </span>
              <span aria-hidden="true" />
            </button>
          </div>
          {loginError ? <p className={`${inlineError} mb-0 mt-[18px]`}>{loginError}</p> : null}
          <p className="mb-0 mt-6 max-w-none text-xs leading-6 text-ink-muted">
            Access is limited to invited team members.
          </p>
        </section>
      </AuthFrame>
    );
  }

  if (queueState.kind === 'checking') {
    return (
      <AuthFrame>
        <CenteredState title="Checking access…" />
      </AuthFrame>
    );
  }

  return (
    <div className="admin-shell min-h-dvh lg:grid lg:grid-cols-[264px_minmax(0,1fr)]">
      <a
        className={`fixed left-4 top-3 z-50 -translate-y-20 rounded-[6px] border border-brass/50 bg-panel-raised px-4 py-2 text-sm font-semibold text-ink shadow-lg transition-transform focus:translate-y-0 ${focusRing}`}
        href="#main-content"
      >
        Skip to content
      </a>
      <header className="admin-chrome flex min-h-[68px] items-center justify-between gap-4 border-b border-brass/25 bg-canvas-deep/85 px-4 py-3.5 backdrop-blur-[18px] sm:min-h-[76px] sm:px-6 lg:hidden">
        <a
          className={`flex items-center text-inherit no-underline ${focusRing}`}
          href="/"
          aria-label="Belore workspace home"
          onClick={(event) => {
            if (
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              selectWorkspaceView('reviews');
            }
          }}
        >
          <img
            className="block h-auto w-28 sm:w-[128px]"
            src="/assets/belore-wordmark-dark.svg"
            alt="Belore"
          />
        </a>
        <div className="flex items-center gap-3 text-[13px] text-ink-soft">
          <span className="hidden max-w-48 truncate sm:inline">{displayName || user.email}</span>
          <button
            className={`${buttonBase} min-h-[38px] border-line/55 bg-transparent text-ink-soft enabled:hover:border-brass/60 enabled:hover:bg-brass/10 enabled:hover:text-ink`}
            onClick={signOutOfWorkspace}
            type="button"
          >
            Sign out
          </button>
        </div>
      </header>
      <WorkspaceSidenav
        accountEmail={user.email}
        accountName={displayName || 'Administrator'}
        activeView={activeView}
        onSelect={selectWorkspaceView}
        onSignOut={signOutOfWorkspace}
      />
      <div className="admin-workspace-frame min-w-0">
        <main
          className="admin-workspace min-w-0 px-4 pb-16 pt-8 sm:px-7 sm:pt-10 lg:px-9 lg:py-6 xl:px-12"
          id="main-content"
          ref={workspaceRef}
          tabIndex={0}
          aria-label="Admin console"
        >
          <div className="mx-auto max-w-[1440px]">
            {activeView === 'catalogue' && (
              <>
                <CataloguePipelineHeading onRefresh={() => void loadCatalogue()} />
                <CatalogueSyncPanel getAccessToken={getAccessToken} onAccessDenied={rejectAccess} />
              </>
            )}
            {activeView === 'contributions' ? (
              <CatalogueContributions
                getAccessToken={getAccessToken}
                onAccessDenied={rejectAccess}
              />
            ) : activeView === 'reports' ? (
              <CommunityReports getAccessToken={getAccessToken} onAccessDenied={rejectAccess} />
            ) : activeView === 'photos' ? (
              <ProfilePhotoModeration
                getAccessToken={getAccessToken}
                onAccessDenied={rejectAccess}
              />
            ) : activeView === 'board' ? (
              <BoardWorkspace getAccessToken={getAccessToken} onAccessDenied={rejectAccess} />
            ) : activeView === 'achievements' ? (
              <AchievementsOverview getAccessToken={getAccessToken} onAccessDenied={rejectAccess} />
            ) : activeView === 'catalogue' ? (
              catalogueState.kind === 'idle' || catalogueState.kind === 'loading' ? (
                <CenteredState title="Loading catalogue pipeline…" />
              ) : catalogueState.kind === 'error' ? (
                <CenteredState
                  action="Try again"
                  onAction={() => void loadCatalogue()}
                  title="Catalogue pipeline could not be loaded"
                >
                  {catalogueState.message}
                </CenteredState>
              ) : (
                <CataloguePipelineDashboard
                  showHeading={false}
                  onRefresh={() => void loadCatalogue()}
                  operations={catalogueState.operations}
                />
              )
            ) : activeView === 'operations' ? (
              operationsState.kind === 'idle' || operationsState.kind === 'loading' ? (
                <CenteredState title="Loading rating operations…" />
              ) : operationsState.kind === 'error' ? (
                <CenteredState
                  action="Try again"
                  onAction={() => void loadOperations()}
                  title="Rating operations could not be loaded"
                >
                  {operationsState.message}
                </CenteredState>
              ) : (
                <RatingOperationsDashboard
                  onRefresh={() => void loadOperations()}
                  operations={operationsState.operations}
                />
              )
            ) : activeView === 'quotes' ? (
              <UnifiedQuoteQueue getAccessToken={getAccessToken} onAccessDenied={rejectAccess} />
            ) : queueState.kind === 'loading' ? (
              <CenteredState title="Loading pending reviews…" />
            ) : queueState.kind === 'error' ? (
              <CenteredState
                action="Try again"
                onAction={() => void loadQueue()}
                title="The review queue could not be loaded"
              >
                {queueState.message}
              </CenteredState>
            ) : (
              <ReviewQueue
                busyDecision={busyDecision}
                error={decisionError}
                filterError={filterError}
                isLoadingMore={isLoadingMore}
                isFiltering={isFiltering}
                loadMoreError={loadMoreError}
                moderatedReviews={moderatedReviews}
                onDecision={(review, value, message) => void moderate(review, value, message)}
                onLoadMore={() => void loadMore()}
                onFiltersChange={(filters) => void applyReviewFilters(filters)}
                queue={queueState.queue}
                reviewerOptions={reviewerOptions}
                selectedFilters={reviewFilters}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export function WorkspaceSidenav({
  accountEmail,
  accountName,
  activeView,
  onSelect,
  onSignOut,
}: {
  accountEmail: string;
  accountName: string;
  activeView: WorkspaceView;
  onSelect(view: WorkspaceView): void;
  onSignOut(): void;
}) {
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const revealSelected = () => {
      const selected = nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (!selected || nav.scrollWidth <= nav.clientWidth) return;
      // Recenter after route changes and when the desktop sidebar becomes a narrow rail.
      nav.scrollLeft +=
        selected.getBoundingClientRect().left -
        nav.getBoundingClientRect().left -
        (nav.clientWidth - selected.offsetWidth) / 2;
    };
    revealSelected();
    const resizeObserver = new ResizeObserver(revealSelected);
    resizeObserver.observe(nav);
    return () => resizeObserver.disconnect();
  }, [activeView]);
  const groups = [
    {
      label: 'Moderation',
      items: [
        { label: 'Review queue', view: 'reviews' },
        { label: quoteModerationCopy.nav, view: 'quotes' },
        { label: COMMUNITY_REPORT_COPY.en.title, view: 'reports' },
        { label: 'Profile photos', view: 'photos' },
        { label: 'Catalogue contributions', view: 'contributions' },
        { label: boardCopy.title, view: 'board' },
      ],
    },
    {
      label: 'Reader journey',
      items: [{ label: 'Achievements', view: 'achievements' }],
    },
    {
      label: 'Operations',
      items: [
        { label: 'Catalogue pipeline', view: 'catalogue' },
        { label: 'Rating operations', view: 'operations' },
      ],
    },
  ] satisfies ReadonlyArray<{
    label: string;
    items: ReadonlyArray<{ label: string; view: WorkspaceView }>;
  }>;
  const accountInitial = (
    accountName.trim()[0] ??
    accountEmail.trim()[0] ??
    'A'
  ).toLocaleUpperCase();

  return (
    <aside className="admin-chrome admin-sidebar">
      <div className="admin-sidebar-brand">
        <a
          className={`text-inherit no-underline ${focusRing}`}
          href="/"
          aria-label="Belore workspace home"
          onClick={(event) => {
            if (
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              onSelect('reviews');
            }
          }}
        >
          <img
            className="block h-auto w-[128px]"
            src="/assets/belore-wordmark-dark.svg"
            alt="Belore"
          />
          <span className="mt-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-ink-muted">
            Admin console
          </span>
        </a>
      </div>
      <nav aria-label="Administration sections" ref={navRef} className="admin-sidebar-nav">
        {groups.map((group) => (
          <div className="admin-sidebar-group" key={group.label}>
            <p className="admin-sidebar-section">{group.label}</p>
            <div className="admin-sidebar-items">
              {group.items.map(({ label, view }) => {
                const active = activeView === view;
                return (
                  <a
                    aria-current={active ? 'page' : undefined}
                    className={`admin-sidebar-link ${focusRing}`}
                    key={view}
                    href={workspacePath(view)}
                    onClick={(event) => {
                      if (
                        event.button === 0 &&
                        !event.metaKey &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey
                      ) {
                        event.preventDefault();
                        onSelect(view);
                      }
                    }}
                  >
                    <span className="admin-sidebar-icon">
                      <WorkspaceNavIcon view={view} />
                    </span>
                    <span className="admin-sidebar-label">{label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="admin-sidebar-footer">
        <div className="admin-account">
          <div className="admin-account-heading">
            <span aria-hidden="true" className="admin-account-avatar">
              {accountInitial}
            </span>
            <strong className="admin-account-name">{accountName}</strong>
          </div>
          <p className="admin-account-email">{accountEmail}</p>
        </div>
        <button className={`admin-sidebar-signout ${focusRing}`} onClick={onSignOut} type="button">
          <span className="admin-sidebar-icon">
            <SignOutIcon />
          </span>
          Sign out
        </button>
      </div>
    </aside>
  );
}

function WorkspaceNavIcon({ view }: { view: WorkspaceView }) {
  const paths: Record<WorkspaceView, ReactNode> = {
    contributions: <path d="M4 4h16v16H4zM8 8h8M8 12h8M8 16h4" />,
    photos: <path d="M4 4h16v16H4zM8 9h.01M4 17l5-5 4 4 3-3 4 4" />,
    reports: <path d="M6 20V4M6 4h11l-2.5 4L17 12H6" />,
    board: <path d="M4 4h16v16H4zM8 8h8M8 12h3M8 16h8" />,
    achievements: (
      <>
        <circle cx="12" cy="9.25" r="4.75" />
        <path d="M9.25 13.25 8 20l4-2 4 2-1.25-6.75" />
      </>
    ),
    reviews: (
      <>
        <path d="M6.5 3.75h7l3 3v11.5h-10z" />
        <path d="M13.5 3.75v3h3M9 11h5M9 14h4" />
      </>
    ),
    quotes: (
      <>
        <path d="M4.75 8.25h5v4.5h-3.5l-1.5 2.5v-7zM12.25 8.25h5v4.5h-3.5l-1.5 2.5v-7z" />
      </>
    ),
    catalogue: (
      <>
        <path d="M4.5 5.25h5.25a2.25 2.25 0 0 1 2.25 2.25v10.25H6.75A2.25 2.25 0 0 1 4.5 15.5z" />
        <path d="M19.5 5.25h-5.25A2.25 2.25 0 0 0 12 7.5v10.25h5.25a2.25 2.25 0 0 0 2.25-2.25z" />
      </>
    ),
    operations: (
      <>
        <path d="M18.25 8.25V4.5h-3.75M5.75 15.75v3.75H9" />
        <path d="M17.25 6.5a7 7 0 0 0-11.5 2M6.75 17.5a7 7 0 0 0 11.5-2" />
      </>
    ),
  };

  return (
    <svg
      aria-hidden="true"
      className="size-[18px]"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
      viewBox="0 0 24 24"
    >
      {paths[view]}
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-[18px]"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
      viewBox="0 0 24 24"
    >
      <path d="M14.5 5.25h-7a2 2 0 0 0-2 2v9.5a2 2 0 0 0 2 2h7M13.75 9l3 3-3 3M9 12h7.75" />
    </svg>
  );
}

function CataloguePipelineHeading({ onRefresh }: { onRefresh(): void }) {
  return (
    <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className={eyebrow}>Operations / Catalogue</p>
        <h1 id="catalogue-pipeline-heading" className={headingOne}>
          Catalogue pipeline
        </h1>
        <p className="mb-0 mt-4 max-w-[72ch] leading-7 text-ink-soft">
          Enrichment queue, external metadata providers, ISBNdb budget, and Open Library edition
          backfills in one operational view.
        </p>
      </div>
      <button
        className={`${buttonBase} border-line/55 bg-transparent text-ink-soft enabled:hover:border-brass/60 enabled:hover:bg-brass/10 enabled:hover:text-ink`}
        onClick={onRefresh}
        type="button"
      >
        Refresh dashboard
      </button>
    </div>
  );
}

export function CataloguePipelineDashboard({
  onRefresh,
  operations,
  showHeading = true,
}: {
  onRefresh(): void;
  operations: CataloguePipelineOperations;
  showHeading?: boolean;
}) {
  const activeJobs = operations.enrichment.counts.pending + operations.enrichment.counts.processing;
  return (
    <section aria-labelledby="catalogue-pipeline-heading">
      {showHeading && <CataloguePipelineHeading onRefresh={onRefresh} />}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Pipeline health">
          <StatusPill status={operations.health.status}>
            {formatHealthStatus(operations.health.status)}
          </StatusPill>
          <p className="mb-0 mt-3 text-sm leading-6 text-ink-soft">
            {operations.health.issues.length === 0
              ? 'No queue, quota, provider, or backfill incident is active.'
              : `${operations.health.issues.length} operational signal${operations.health.issues.length === 1 ? '' : 's'} need attention.`}
          </p>
        </MetricCard>
        <MetricCard label="Enrichment queue">
          <strong className="block font-serif text-2xl font-medium text-ink">
            {activeJobs} active
          </strong>
          <p className="mb-0 mt-3 text-sm leading-6 text-ink-muted">
            {operations.enrichment.duePending} due · {operations.enrichment.expiredLeases} expired
            leases · {operations.enrichment.failedLast24Hours} failed today
          </p>
        </MetricCard>
        <MetricCard label="ISBNdb budget">
          <strong className="block font-serif text-2xl font-medium text-ink">
            {operations.quota.usedToday} / {operations.quota.dailyLimit}
          </strong>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-canvas-deep">
            <span
              className={`block h-full rounded-full ${operations.quota.utilizationPercent >= 80 ? 'bg-warning' : 'bg-thread'}`}
              style={{ width: `${operations.quota.utilizationPercent}%` }}
            />
          </div>
          <p className="mb-0 mt-3 text-xs leading-5 text-ink-muted">
            {operations.quota.remainingToday} units remain today
          </p>
        </MetricCard>
        <MetricCard label="Backfill opportunity">
          <strong className="block font-serif text-2xl font-medium text-ink">
            {operations.backfill.inventory.incompleteWithIsbn}
          </strong>
          <p className="mb-0 mt-3 text-sm leading-6 text-ink-muted">
            incomplete Open Library editions with an ISBN ·{' '}
            {operations.backfill.inventory.referencedIncomplete} reader-linked
          </p>
        </MetricCard>
      </div>

      {operations.health.issues.length > 0 ? (
        <section className="mt-6 rounded-[17px] border border-danger/45 bg-danger-fill/30 p-5 sm:p-6">
          <p className={`${eyebrow} mb-2 text-warning`}>Active signals</p>
          <ul className="m-0 grid gap-2 pl-5 text-sm leading-6 text-ink-soft md:grid-cols-2">
            {operations.health.issues.map((issue) => (
              <li key={issue.code}>{catalogueIssueCopy(issue.code)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-6 rounded-[17px] border border-line/75 bg-panel/80 p-5 shadow-panel sm:p-6">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`${eyebrow} mb-1`}>Background worker</p>
            <h2 className="m-0 font-serif text-2xl font-medium text-ink">Enrichment queue</h2>
          </div>
          <p className="m-0 text-xs text-ink-muted">
            Last completed {formatTimestamp(operations.enrichment.latestCompletedAt)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {(
            [
              ['Pending', operations.enrichment.counts.pending],
              ['Processing', operations.enrichment.counts.processing],
              ['Succeeded', operations.enrichment.counts.succeeded],
              ['Not found', operations.enrichment.counts.notFound],
              ['Conflict', operations.enrichment.counts.conflict],
              ['Failed', operations.enrichment.counts.failed],
            ] as const
          ).map(([label, value]) => (
            <div
              className="rounded-[10px] border border-line/35 bg-canvas-deep/45 px-4 py-4"
              key={label}
            >
              <span className="block text-xs font-semibold uppercase tracking-[0.08em] text-ink-muted">
                {label}
              </span>
              <strong className="mt-2 block font-serif text-2xl font-medium text-ink">
                {value}
              </strong>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-[17px] border border-line/75 bg-panel/80 shadow-panel">
        <div className="border-b border-line/30 px-5 py-4 sm:px-6">
          <p className={`${eyebrow} mb-1`}>Last {operations.providers.lookbackDays} days</p>
          <h2 className="m-0 font-serif text-2xl font-medium text-ink">Metadata providers</h2>
        </div>
        {operations.providers.items.length === 0 ? (
          <p className="m-0 px-5 py-8 text-sm text-ink-soft sm:px-6">
            Provider telemetry will appear after the first catalogue lookup on this release.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-left text-sm">
              <thead className="bg-canvas-deep/55 text-xs uppercase tracking-[0.08em] text-ink-muted">
                <tr>
                  <th className="px-5 py-3 font-semibold sm:px-6">Provider</th>
                  <th className="px-5 py-3 font-semibold">Health</th>
                  <th className="px-5 py-3 font-semibold">Attempts</th>
                  <th className="px-5 py-3 font-semibold">Outcomes</th>
                  <th className="px-5 py-3 font-semibold">Latency</th>
                  <th className="px-5 py-3 font-semibold">Last attempt</th>
                </tr>
              </thead>
              <tbody>
                {operations.providers.items.map((provider) => (
                  <tr className="border-t border-line/25" key={provider.provider}>
                    <td className="px-5 py-4 font-semibold text-ink sm:px-6">
                      {formatProviderName(provider.provider)}
                    </td>
                    <td className="px-5 py-4">
                      <StatusPill status={provider.status}>
                        {provider.status === 'unknown'
                          ? 'No data'
                          : `${provider.availabilityPercent ?? 0}% available`}
                      </StatusPill>
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {provider.attempts} · {provider.requestItems} items
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {provider.hitAttempts} hit · {provider.notFoundAttempts} miss ·{' '}
                      {provider.unavailableAttempts} unavailable
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {formatLatency(provider.averageDurationMs)} avg ·{' '}
                      {formatLatency(provider.maxDurationMs)} max
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-muted">
                      {formatTimestamp(provider.lastAttemptAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-6 overflow-hidden rounded-[17px] border border-line/75 bg-panel/80 shadow-panel">
        <div className="flex flex-col gap-2 border-b border-line/30 px-5 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-6">
          <div>
            <p className={`${eyebrow} mb-1`}>Open Library → ISBNdb</p>
            <h2 className="m-0 font-serif text-2xl font-medium text-ink">Metadata backfill</h2>
          </div>
          <p className="m-0 text-xs text-ink-muted">
            Sundays 23:30 Europe/Bratislava · {operations.backfill.state.tracked} tracked ·{' '}
            {operations.backfill.state.due} due for retry
          </p>
        </div>
        {operations.backfill.recentRuns.length === 0 ? (
          <p className="m-0 px-5 py-8 text-sm text-ink-soft sm:px-6">
            The backfill jobs are deployed, but no audited preview or execution has run yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead className="bg-canvas-deep/55 text-xs uppercase tracking-[0.08em] text-ink-muted">
                <tr>
                  <th className="px-5 py-3 font-semibold sm:px-6">Started</th>
                  <th className="px-5 py-3 font-semibold">Mode</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Selection</th>
                  <th className="px-5 py-3 font-semibold">Result</th>
                  <th className="px-5 py-3 font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                {operations.backfill.recentRuns.map((run) => (
                  <tr className="border-t border-line/25" key={run.id}>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-soft sm:px-6">
                      {formatTimestamp(run.startedAt)}
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {run.mode === 'execute' ? 'Execute' : 'Preview'}
                    </td>
                    <td className="px-5 py-4">
                      <StatusPill
                        status={
                          run.status === 'failed'
                            ? 'critical'
                            : run.status === 'running'
                              ? 'degraded'
                              : 'healthy'
                        }
                      >
                        {formatRunStatus(run.status)}
                      </StatusPill>
                      {run.errorCode ? (
                        <span className="mt-1 block font-mono text-xs text-warning">
                          {run.errorCode}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {run.selectedRows} rows · {run.selectedIsbns} ISBNs
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {run.result.enrichedRows} enriched · {run.result.notFoundRows} not found ·{' '}
                      {run.result.requestedQuotaUnits} units
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-muted">
                      {formatDuration(run.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="mb-0 mt-5 text-xs leading-5 text-ink-muted">
        The execute backfill runs weekly on Sunday at 23:30 Europe/Bratislava and processes at most
        1,000 unique ISBNs in batches of 100, with one paid HTTP attempt per batch and 250 daily
        units reserved for production. The watchdog runs every three hours and recovers missed
        enrichment dispatches. It marks enrichment work critical after{' '}
        {operations.watchdog.enrichmentOverdueMinutes} minutes and a running backfill stalled after{' '}
        {operations.watchdog.backfillStuckMinutes} minutes.
      </p>
    </section>
  );
}

function StatusPill({
  children,
  status,
}: {
  children: ReactNode;
  status: CataloguePipelineOperations['health']['status'] | 'unknown';
}) {
  const classes =
    status === 'healthy'
      ? 'border-safe/35 bg-safe-fill text-safe'
      : status === 'degraded'
        ? 'border-thread/35 bg-thread-soft text-thread'
        : status === 'unknown'
          ? 'border-line/45 bg-canvas-deep/55 text-ink-muted'
          : 'border-danger/45 bg-danger-fill/45 text-warning';
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-bold ${classes}`}>
      {children}
    </span>
  );
}

function formatHealthStatus(status: CataloguePipelineOperations['health']['status']) {
  return status === 'healthy' ? 'Healthy' : status === 'degraded' ? 'Degraded' : 'Critical';
}

function catalogueIssueCopy(code: CataloguePipelineIssueCode) {
  const copy: Record<CataloguePipelineIssueCode, string> = {
    backfill_latest_failed: 'The latest metadata backfill failed.',
    backfill_stuck: 'A metadata backfill exceeded its heartbeat window.',
    enrichment_failed_jobs: 'Enrichment jobs failed during the last 24 hours.',
    enrichment_lease_expired: 'An enrichment worker lease expired before completion.',
    enrichment_overdue: 'Due enrichment work exceeded the 15-minute service window.',
    provider_unavailable: 'A metadata provider reported unavailable attempts today.',
    quota_exhausted: 'ISBNdb usage reached at least 95% of the daily budget.',
    quota_near_limit: 'ISBNdb usage reached at least 80% of the daily budget.',
  };
  return copy[code];
}

function formatProviderName(provider: string) {
  const names: Record<string, string> = {
    google_books: 'Google Books',
    isbndb: 'ISBNdb',
    knihovny_cz: 'Knihovny.cz',
    open_library: 'Open Library',
  };
  return names[provider] ?? provider;
}

function formatLatency(value: number | null) {
  if (value === null) {
    return '—';
  }
  return value >= 1_000 ? `${(value / 1_000).toFixed(1)} s` : `${Math.round(value)} ms`;
}

export function RatingOperationsDashboard({
  onRefresh,
  operations,
}: {
  onRefresh(): void;
  operations: OpenLibraryRatingOperations;
}) {
  const healthCopy = ratingHealthCopy(operations);
  return (
    <section aria-labelledby="rating-operations-heading">
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className={eyebrow}>Operations / Open Library</p>
          <h1 id="rating-operations-heading" className={headingOne}>
            Rating refresh
          </h1>
          <p className="mb-0 mt-4 max-w-[68ch] leading-7 text-ink-soft">
            Monthly book and author rating imports, with a daily watchdog for failed, stale, or
            stalled runs.
          </p>
        </div>
        <button
          className={`${buttonBase} border-line/55 bg-transparent text-ink-soft enabled:hover:border-brass/60 enabled:hover:bg-brass/10 enabled:hover:text-ink`}
          onClick={onRefresh}
          type="button"
        >
          Refresh dashboard
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Health">
          <span
            className={`inline-flex rounded-full border px-3 py-1 text-sm font-bold ${ratingHealthClass(
              operations.health.status,
            )}`}
          >
            {healthCopy.label}
          </span>
          <p className="mb-0 mt-3 text-sm leading-6 text-ink-soft">{healthCopy.detail}</p>
        </MetricCard>
        <MetricCard label="Last successful run">
          <strong className="block font-serif text-2xl font-medium text-ink">
            {formatTimestamp(operations.lastSuccess?.completedAt ?? null)}
          </strong>
          <p className="mb-0 mt-3 text-sm text-ink-muted">
            {formatDuration(operations.lastSuccess?.durationMs ?? null)}
          </p>
        </MetricCard>
        <CoverageCard label="Book works" coverage={operations.coverage.works} />
        <CoverageCard label="Authors" coverage={operations.coverage.authors} />
      </div>

      <section className="mt-6 rounded-[17px] border border-line/75 bg-panel/80 p-5 shadow-panel sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
          <div>
            <p className={`${eyebrow} mb-1`}>Schedule</p>
            <h2 className="m-0 font-serif text-2xl font-medium text-ink">
              03:17 UTC on the first day of every month
            </h2>
          </div>
          <code className="w-fit rounded border border-line/45 bg-canvas-deep px-2.5 py-1 text-xs text-brass-pale">
            {operations.schedule.cron}
          </code>
        </div>
        <p className="mb-0 mt-3 text-sm leading-6 text-ink-soft">
          The watchdog marks the refresh critical after {operations.schedule.staleAfterDays} days
          without a success or {operations.schedule.stuckAfterMinutes} minutes without a heartbeat.
        </p>
      </section>

      <section className="mt-6 overflow-hidden rounded-[17px] border border-line/75 bg-panel/80 shadow-panel">
        <div className="border-b border-line/30 px-5 py-4 sm:px-6">
          <p className={`${eyebrow} mb-1`}>Audit trail</p>
          <h2 className="m-0 font-serif text-2xl font-medium text-ink">Recent runs</h2>
        </div>
        {operations.recentRuns.length === 0 ? (
          <p className="m-0 px-5 py-8 text-sm text-ink-soft sm:px-6">
            No audited rating refresh has run yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead className="bg-canvas-deep/55 text-xs uppercase tracking-[0.08em] text-ink-muted">
                <tr>
                  <th className="px-5 py-3 font-semibold sm:px-6">Started</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Duration</th>
                  <th className="px-5 py-3 font-semibold">Works</th>
                  <th className="px-5 py-3 font-semibold">Authors</th>
                  <th className="px-5 py-3 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {operations.recentRuns.map((run) => (
                  <tr className="border-t border-line/25" key={run.id}>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-soft sm:px-6">
                      {formatTimestamp(run.startedAt)}
                    </td>
                    <td className="px-5 py-4 font-semibold text-ink">
                      {formatRunStatus(run.status)}
                      {run.errorCode ? (
                        <span className="mt-1 block font-mono text-xs font-normal text-warning">
                          {run.errorCode}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-soft">
                      {formatDuration(run.durationMs)}
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {formatRunCounts(run.result.works, run.candidates.works)}
                    </td>
                    <td className="px-5 py-4 text-ink-soft">
                      {formatRunCounts(run.result.authors, run.candidates.authors)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-ink-muted">
                      {run.source === 'cloud_run_job' ? 'Cloud Run' : 'Operator'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}

function MetricCard({ children, label }: { children: ReactNode; label: string }) {
  return (
    <section className="rounded-[17px] border border-line/75 bg-panel p-5 shadow-panel sm:p-6">
      <p className={`${eyebrow} mb-3`}>{label}</p>
      {children}
    </section>
  );
}

function CoverageCard({
  coverage,
  label,
}: {
  coverage: OpenLibraryRatingOperations['coverage']['works'];
  label: string;
}) {
  const percent = coverage.total === 0 ? 0 : Math.round((coverage.mapped / coverage.total) * 100);
  return (
    <MetricCard label={label}>
      <strong className="block font-serif text-2xl font-medium text-ink">
        {coverage.mapped} / {coverage.total}
      </strong>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-canvas-deep">
        <span className="block h-full rounded-full bg-thread" style={{ width: `${percent}%` }} />
      </div>
      <p className="mb-0 mt-3 text-xs leading-5 text-ink-muted">
        {percent}% mapped · {coverage.rated} rated · {coverage.unresolved} unresolved ·{' '}
        {coverage.due} due
      </p>
    </MetricCard>
  );
}

function ratingHealthCopy(operations: OpenLibraryRatingOperations) {
  switch (operations.health.reason) {
    case 'healthy':
      return { label: 'Healthy', detail: 'The latest scheduled refresh completed successfully.' };
    case 'refresh_running':
      return { label: 'Running', detail: 'A refresh is active and its heartbeat is current.' };
    case 'refresh_stuck':
      return { label: 'Critical', detail: 'The active refresh stopped sending heartbeats.' };
    case 'latest_attempt_failed':
      return { label: 'Critical', detail: 'The latest refresh attempt failed.' };
    case 'success_stale':
      return {
        label: 'Critical',
        detail: 'No successful refresh was recorded within the expected interval.',
      };
    case 'never_succeeded':
      return {
        label: operations.health.status === 'never_run' ? 'Not run' : 'Critical',
        detail: 'No successful audited refresh is available yet.',
      };
  }
}

function ratingHealthClass(status: OpenLibraryRatingOperations['health']['status']) {
  if (status === 'healthy') {
    return 'border-safe/35 bg-safe-fill text-safe';
  }
  if (status === 'running') {
    return 'border-thread/35 bg-thread-soft text-thread';
  }
  return 'border-danger/45 bg-danger-fill/45 text-warning';
}

function formatTimestamp(value: string | null) {
  if (!value) {
    return 'Not yet';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatDuration(value: number | null) {
  if (value === null) {
    return 'In progress';
  }
  if (value < 60_000) {
    return `${Math.max(1, Math.round(value / 1_000))} sec`;
  }
  return `${Math.round(value / 60_000)} min`;
}

function formatRunStatus(status: 'failed' | 'running' | 'succeeded') {
  return status === 'succeeded' ? 'Succeeded' : status === 'failed' ? 'Failed' : 'Running';
}

function formatRunCounts(
  counts: { deferred: number; refreshed: number; unresolved: number },
  candidates: number,
) {
  return `${counts.refreshed} refreshed · ${counts.unresolved} unresolved · ${counts.deferred} deferred / ${candidates}`;
}

export function QuoteQueue({
  busyDecision,
  error,
  filterError,
  isFiltering,
  isLoadingMore,
  loadMoreError,
  moderatedQuotes,
  onDecision,
  onFiltersChange,
  onLoadMore,
  queue,
  readerOptions,
  selectedFilters,
}: {
  busyDecision: BusyDecision | null;
  error: DecisionError | null;
  filterError: string | null;
  isFiltering: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  moderatedQuotes: ModeratedReviews;
  onDecision(quote: PendingQuote, decision: ReviewDecision, moderatorMessage?: string | null): void;
  onFiltersChange(filters: AdminReviewFilters): void;
  onLoadMore(): void;
  queue: PendingQuoteQueue;
  readerOptions: PendingReviewerOption[];
  selectedFilters: AdminReviewFilters;
}) {
  const handledCount = Object.keys(moderatedQuotes).length;
  const hasActiveFilters =
    selectedFilters.reviewerIds.length > 0 || selectedFilters.checkStatus !== null;
  return (
    <>
      <PageHeading
        contentNoun="quotes"
        handledCount={handledCount}
        loadedCount={queue.quotes.length}
        total={queue.total}
        visibleCount={queue.quotes.length}
      />
      <ReviewFilters
        contentType="Quote"
        onChange={onFiltersChange}
        reviewerOptions={readerOptions}
        selectedFilters={selectedFilters}
      />
      {filterError ? (
        <p className={`${inlineError} mb-5 mt-[-0.5rem]`} role="alert">
          The filters could not be applied. {filterError}
        </p>
      ) : null}
      {isFiltering ? (
        <p aria-live="polite" className="mb-5 text-sm text-ink-muted">
          Applying filters…
        </p>
      ) : null}
      {queue.quotes.length > 0 ? (
        <div aria-busy={isFiltering} className="columns-1 gap-5 lg:columns-2 lg:gap-6">
          {queue.quotes.map((quote) => (
            <QuoteCard
              busyDecision={busyDecision}
              error={error?.reviewId === quote.id ? error.message : null}
              key={quote.id}
              moderatedDecision={moderatedQuotes[quote.id] ?? null}
              onDecision={onDecision}
              quote={quote}
            />
          ))}
        </div>
      ) : (
        <section className={`${stateCard} max-w-none`} aria-live="polite">
          <h2 className="m-0 font-serif text-2xl text-ink">
            {hasActiveFilters ? 'No quotes match these filters' : 'No quotes are waiting'}
          </h2>
          <p className="mb-0 mt-2 max-w-[58ch] text-sm leading-6 text-ink-soft">
            {hasActiveFilters
              ? 'Change or clear the filters to return to the full queue.'
              : 'The public quote moderation queue is clear.'}
          </p>
        </section>
      )}
      <InfiniteQueueSentinel
        contentNoun="quotes"
        error={loadMoreError}
        hasMore={!isFiltering && queue.nextCursor !== null}
        isLoading={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </>
  );
}

export function QuoteCard<T extends Omit<PendingQuote, 'readerId'>>({
  busyDecision,
  error,
  moderatedDecision,
  onDecision,
  quote,
  category = 'Shared quote request',
  showCaptureDetails = true,
  contentLabel = 'Quote',
  readerSummary,
  details,
  actions,
}: {
  busyDecision: BusyDecision | null;
  error: string | null;
  moderatedDecision: ReviewDecision | null;
  onDecision(quote: T, decision: ReviewDecision, moderatorMessage?: string | null): void;
  quote: T;
  category?: string;
  showCaptureDetails?: boolean;
  contentLabel?: string;
  readerSummary?: string;
  details?: ReactNode;
  actions?: ReactNode;
}) {
  const isBusy = busyDecision?.reviewId === quote.id;
  const titleId = `quote-title-${quote.id}`;
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [rejectionValidationError, setRejectionValidationError] = useState<string | null>(null);

  function submitRejection() {
    const validationError = moderatorMessageValidationError(rejectionMessage, 'quote');
    if (validationError) {
      setRejectionValidationError(validationError);
      return;
    }
    setRejectionValidationError(null);
    onDecision(quote, 'rejected', normalizeModeratorMessage(rejectionMessage));
  }

  return (
    <article
      aria-labelledby={titleId}
      className="mb-5 inline-flex w-full min-w-0 break-inside-avoid flex-col overflow-hidden rounded-[17px] border border-line/75 bg-panel align-top shadow-panel lg:mb-6"
      data-moderation-state={moderatedDecision ?? 'pending'}
      data-quote-id={quote.id}
    >
      <div className="border-b border-line/30 p-5 sm:p-6">
        <div className="flex min-w-0 items-start gap-4">
          <div className="grid h-[114px] w-[76px] shrink-0 place-items-center overflow-hidden rounded-[12px] border border-line/75 bg-canvas-deep text-center text-[9px] leading-3 text-ink-muted shadow-sm">
            {quote.book.coverImageUrl ? (
              <img
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                src={quote.book.coverImageUrl}
              />
            ) : (
              <span aria-hidden="true">No cover</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`${eyebrow} mb-2`}>{category}</p>
            <h2
              className="m-0 font-serif text-[clamp(1.3rem,2.5vw,1.65rem)] font-medium leading-[1.12] tracking-[-0.02em] text-balance"
              id={titleId}
            >
              {quote.book.title}
            </h2>
            <p className="mb-0 mt-2 line-clamp-2 text-sm leading-5 text-ink-muted">
              {quote.book.authors.join(', ')}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              <BookFact
                label={quote.book.isbn13 ? 'ISBN-13' : 'ISBN-10'}
                monospaced
                value={quote.book.isbn13 ?? quote.book.isbn10 ?? 'Not available'}
                wide
              />
              <BookFact
                label="Published"
                value={
                  [quote.book.publisher, quote.book.publishedYear].filter(Boolean).join(' · ') ||
                  'Not available'
                }
              />
              <BookFact label="Language" value={quote.language.toUpperCase()} />
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-1.5 border-t border-line/25 pt-4 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4">
          <p className="m-0 min-w-0 truncate font-semibold text-ink-soft">
            {readerSummary ?? quote.readerName ?? 'Unnamed reader'}
          </p>
          <time className="text-ink-muted sm:text-right" dateTime={quote.submittedAt}>
            {new Intl.DateTimeFormat(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(new Date(quote.submittedAt))}
          </time>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        {details}
        <blockquote
          aria-label={`${contentLabel} text for ${quote.book.title}`}
          className="review-paper m-0 [overflow-wrap:anywhere] whitespace-pre-wrap px-6 pb-7 pt-7 font-serif text-[1.0625rem] leading-[1.7] text-paper-text text-pretty sm:px-7 sm:pb-8 sm:pt-8"
        >
          <p className="relative z-[3] m-0">{quote.body}</p>
          {showCaptureDetails && (
            <footer
              aria-label="Reported quote page"
              className="relative z-[3] mt-6 flex items-center justify-between gap-4 border-t border-paper-text/15 pt-4 text-xs"
            >
              <span className="font-sans font-bold uppercase tracking-[0.09em] text-paper-text-muted">
                Reported page
              </span>
              <span className="font-sans font-semibold tabular-nums text-paper-text-muted">
                {quote.pageNumber === null ? 'Not provided' : `Page ${quote.pageNumber}`}
              </span>
            </footer>
          )}
        </blockquote>
        {showCaptureDetails && (
          <AutomatedContentCheck check={quote.contentCheck} contentType="quote" />
        )}
        {error ? <p className={inlineError}>{error}</p> : null}
      </div>
      <div className="border-t border-line/30 bg-canvas-deep/30 px-5 py-5 sm:px-6">
        {actions ??
          (moderatedDecision ? (
            <div
              aria-live="polite"
              className={`grid min-h-11 place-items-center rounded-[12px] border px-4 py-2.5 text-center text-sm font-bold ${
                moderatedDecision === 'approved'
                  ? 'border-safe/35 bg-safe/10 text-safe'
                  : 'border-danger/40 bg-danger-fill/30 text-warning'
              }`}
            >
              {moderatedDecision === 'approved'
                ? 'Approved · kept here until reload'
                : 'Declined · kept here until reload'}
            </div>
          ) : rejectionOpen ? (
            <RejectionComposer
              busy={busyDecision !== null}
              contentType="quote"
              error={rejectionValidationError}
              message={rejectionMessage}
              onCancel={() => {
                setRejectionOpen(false);
                setRejectionValidationError(null);
              }}
              onChange={(value) => {
                setRejectionMessage(value);
                setRejectionValidationError(null);
              }}
              onSubmit={submitRejection}
              reviewId={quote.id}
              reviewTitle={quote.book.title}
              submitting={isBusy && busyDecision.decision === 'rejected'}
            />
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <button
                aria-label={`Decline quote for ${quote.book.title}`}
                className={`${buttonBase} border-danger/50 bg-danger-fill/20 text-warning enabled:hover:bg-danger-fill/45`}
                disabled={busyDecision !== null}
                onClick={() => setRejectionOpen(true)}
                type="button"
              >
                Decline
              </button>
              <button
                aria-label={`Approve quote for ${quote.book.title}`}
                className={primaryButton}
                disabled={busyDecision !== null}
                onClick={() => onDecision(quote, 'approved', null)}
                type="button"
              >
                {isBusy && busyDecision.decision === 'approved' ? 'Approving…' : 'Approve'}
              </button>
            </div>
          ))}
      </div>
    </article>
  );
}

export function ReviewQueue({
  busyDecision,
  error,
  filterError,
  isFiltering,
  isLoadingMore,
  loadMoreError,
  moderatedReviews,
  onDecision,
  onLoadMore,
  onFiltersChange,
  queue,
  reviewerOptions,
  selectedFilters,
}: {
  busyDecision: BusyDecision | null;
  error: DecisionError | null;
  filterError: string | null;
  isFiltering: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  moderatedReviews: ModeratedReviews;
  onDecision(
    review: PendingReview,
    decision: ReviewDecision,
    moderatorMessage?: string | null,
  ): void;
  onLoadMore(): void;
  onFiltersChange(filters: AdminReviewFilters): void;
  queue: PendingReviewQueue;
  reviewerOptions: PendingReviewerOption[];
  selectedFilters: AdminReviewFilters;
}) {
  const handledCount = Object.keys(moderatedReviews).length;
  const hasActiveFilters =
    selectedFilters.reviewerIds.length > 0 || selectedFilters.checkStatus !== null;
  return (
    <>
      <PageHeading
        contentNoun="reviews"
        handledCount={handledCount}
        loadedCount={queue.reviews.length}
        total={queue.total}
        visibleCount={queue.reviews.length}
      />
      <ReviewFilters
        contentType="Review"
        onChange={onFiltersChange}
        reviewerOptions={reviewerOptions}
        selectedFilters={selectedFilters}
      />
      {filterError ? (
        <p className={`${inlineError} mb-5 mt-[-0.5rem]`} role="alert">
          The filters could not be applied. {filterError}
        </p>
      ) : null}
      {isFiltering ? (
        <p aria-live="polite" className="mb-5 text-sm text-ink-muted">
          Applying filters…
        </p>
      ) : null}
      {queue.reviews.length > 0 ? (
        <div aria-busy={isFiltering} className="columns-1 gap-5 lg:columns-2 lg:gap-6">
          {queue.reviews.map((review) => (
            <ReviewCard
              busyDecision={busyDecision}
              error={error?.reviewId === review.id ? error.message : null}
              key={review.id}
              moderatedDecision={moderatedReviews[review.id] ?? null}
              onDecision={onDecision}
              review={review}
            />
          ))}
        </div>
      ) : (
        <section className={`${stateCard} max-w-none`} aria-live="polite">
          <h2 className="m-0 font-serif text-2xl text-ink">
            {hasActiveFilters ? 'No reviews match these filters' : 'No reviews are waiting'}
          </h2>
          <p className="mb-0 mt-2 max-w-[58ch] text-sm leading-6 text-ink-soft">
            {hasActiveFilters
              ? 'Change or clear the filters to return to the full queue.'
              : 'The public review moderation queue is clear.'}
          </p>
        </section>
      )}
      <InfiniteQueueSentinel
        contentNoun="reviews"
        error={loadMoreError}
        hasMore={!isFiltering && queue.nextCursor !== null}
        isLoading={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </>
  );
}

export function ReviewFilters({
  contentType,
  onChange,
  reviewerOptions,
  selectedFilters,
}: {
  contentType: 'Quote' | 'Review';
  onChange(filters: AdminReviewFilters): void;
  reviewerOptions: PendingReviewerOption[];
  selectedFilters: AdminReviewFilters;
}) {
  const idPrefix = contentType.toLocaleLowerCase();
  const [reviewerQuery, setReviewerQuery] = useState('');
  const filteredReviewerOptions = filterReviewerOptions(reviewerOptions, reviewerQuery);
  const selectedReviewerLabel = (() => {
    if (selectedFilters.reviewerIds.length === 0) {
      return 'All readers';
    }
    if (selectedFilters.reviewerIds.length === 1) {
      const selectedId = selectedFilters.reviewerIds[0];
      return (
        reviewerOptions.find((option) => option.id === selectedId)?.name ?? '1 reader selected'
      );
    }
    return `${selectedFilters.reviewerIds.length} readers selected`;
  })();
  const statuses: Array<{
    label: string;
    value: ReviewCheckStatus | null;
  }> = [
    { label: 'All statuses', value: null },
    { label: 'No signals', value: 'clear' },
    { label: 'Review needed', value: 'flagged' },
    { label: 'Unavailable', value: 'unavailable' },
  ];
  const selectedStatusLabel =
    statuses.find((status) => status.value === selectedFilters.checkStatus)?.label ??
    'All statuses';
  return (
    <section
      aria-label={`${contentType} filters`}
      className="mb-6 grid max-w-[52rem] gap-4 sm:grid-cols-2 sm:items-start"
    >
      <div className="grid min-w-0 gap-2 text-xs font-bold uppercase tracking-[0.08em] text-brass-pale">
        <span id={`${idPrefix}-reader-filter-label`}>Readers</span>
        <FilterSelectBox
          describedBy={`${idPrefix}-reader-filter-help`}
          labelId={`${idPrefix}-reader-filter-label`}
          panelId={`${idPrefix}-reader-filter-options`}
          valueLabel={selectedReviewerLabel}
        >
          {() => (
            <>
              <label className="sr-only" htmlFor={`${idPrefix}-reader-filter-search`}>
                Find reader
              </label>
              <input
                autoComplete="off"
                className={`min-h-10 w-full rounded-[6px] border border-control-line bg-panel-raised px-3 py-2 text-sm font-medium normal-case tracking-normal text-ink placeholder:text-ink-muted ${focusRing}`}
                id={`${idPrefix}-reader-filter-search`}
                onChange={(event) => setReviewerQuery(event.currentTarget.value)}
                placeholder="Find reader…"
                type="search"
                value={reviewerQuery}
              />
              <div
                aria-label="Readers"
                aria-multiselectable="true"
                className="mt-2 max-h-64 overflow-y-auto overscroll-contain"
                role="listbox"
              >
                {filteredReviewerOptions.length > 0 ? (
                  filteredReviewerOptions.map((option) => {
                    const isSelected = selectedFilters.reviewerIds.includes(option.id);
                    return (
                      <label
                        aria-selected={isSelected}
                        className="flex cursor-pointer items-center gap-2.5 rounded-[5px] px-2 py-2 text-sm font-medium normal-case tracking-normal text-ink-soft transition-colors duration-[160ms] hover:bg-brass/10 hover:text-ink"
                        key={option.id}
                        role="option"
                      >
                        <input
                          checked={isSelected}
                          className={`size-4 shrink-0 accent-thread ${focusRing}`}
                          onChange={() => {
                            const reviewerIds = isSelected
                              ? selectedFilters.reviewerIds.filter(
                                  (reviewerId) => reviewerId !== option.id,
                                )
                              : [...selectedFilters.reviewerIds, option.id];
                            onChange({ ...selectedFilters, reviewerIds });
                          }}
                          type="checkbox"
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {option.name ?? 'Unnamed reader'}
                        </span>
                        <span className="shrink-0 font-mono text-xs tabular-nums text-ink-muted">
                          {option.pendingReviewCount}
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <p className="m-0 px-2 py-3 text-sm font-medium normal-case tracking-normal text-ink-muted">
                    No readers found
                  </p>
                )}
              </div>
            </>
          )}
        </FilterSelectBox>
        <span
          className="text-[11px] font-normal normal-case leading-4 tracking-normal text-ink-muted"
          id={`${idPrefix}-reader-filter-help`}
        >
          {selectedFilters.reviewerIds.length === 0
            ? 'All readers are included. Select one or more to narrow the queue.'
            : `${selectedFilters.reviewerIds.length} selected`}
        </span>
      </div>
      <div className="grid min-w-0 gap-2 text-xs font-bold uppercase tracking-[0.08em] text-brass-pale">
        <span id={`${idPrefix}-status-filter-label`}>Automated check</span>
        <FilterSelectBox
          labelId={`${idPrefix}-status-filter-label`}
          panelId={`${idPrefix}-status-filter-options`}
          valueLabel={selectedStatusLabel}
        >
          {(close) => (
            <div aria-label="Automated check" role="listbox">
              {statuses.map((status) => {
                const isSelected = selectedFilters.checkStatus === status.value;
                return (
                  <button
                    aria-selected={isSelected}
                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-[5px] border-0 bg-transparent px-2 py-2 text-left text-sm font-medium normal-case tracking-normal text-ink-soft transition-colors duration-[160ms] hover:bg-brass/10 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-thread"
                    key={status.value ?? 'all'}
                    onClick={() => {
                      if (!isSelected) {
                        onChange({
                          ...selectedFilters,
                          checkStatus: status.value,
                        });
                      }
                      close();
                    }}
                    role="option"
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className={`grid size-4 shrink-0 place-items-center rounded-full border ${isSelected ? 'border-thread bg-thread' : 'border-line/70'}`}
                    >
                      {isSelected ? <span className="size-1.5 rounded-full bg-on-primary" /> : null}
                    </span>
                    {status.label}
                  </button>
                );
              })}
            </div>
          )}
        </FilterSelectBox>
      </div>
    </section>
  );
}

function FilterSelectBox({
  children,
  describedBy,
  labelId,
  panelId,
  valueLabel,
}: {
  children(close: () => void): ReactNode;
  describedBy?: string;
  labelId: string;
  panelId: string;
  valueLabel: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  const valueId = `${panelId}-value`;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, isOpen]);

  return (
    <div className="relative min-w-0" data-filter-selectbox ref={root}>
      <button
        aria-controls={panelId}
        aria-describedby={describedBy}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={`${labelId} ${valueId}`}
        className={`flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-[12px] border border-control-line bg-panel-raised px-3.5 py-2.5 text-left text-sm font-semibold normal-case tracking-normal text-ink-soft transition-colors duration-[180ms] hover:border-brass/60 ${isOpen ? 'border-brass/60' : ''} ${focusRing}`}
        onClick={() => setIsOpen((open) => !open)}
        ref={trigger}
        type="button"
      >
        <span className="truncate" id={valueId}>
          {valueLabel}
        </span>
        <svg
          aria-hidden="true"
          className={`size-4 shrink-0 text-brass-pale transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 16 16"
        >
          <path
            d="m4 6 4 4 4-4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </svg>
      </button>
      <div
        className="absolute z-20 mt-2 w-full min-w-[16rem] rounded-[12px] border border-line/75 bg-panel-raised p-2.5 shadow-panel"
        hidden={!isOpen}
        id={panelId}
      >
        {children(close)}
      </div>
    </div>
  );
}

function InfiniteQueueSentinel({
  contentNoun,
  error,
  hasMore,
  isLoading,
  onLoadMore,
}: {
  contentNoun: 'quotes' | 'reviews';
  error: string | null;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore(): void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasMore || isLoading || error || !sentinel.current) {
      return;
    }
    const target = sentinel.current;
    const desktop = window.matchMedia('(min-width: 1024px)');
    let observer: IntersectionObserver;
    const observeScrollArea = () => {
      observer?.disconnect();
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) onLoadMore();
        },
        {
          root: desktop.matches ? target.closest('.admin-workspace') : null,
          rootMargin: '600px 0px',
        },
      );
      observer.observe(target);
    };
    observeScrollArea();
    desktop.addEventListener('change', observeScrollArea);
    return () => {
      observer.disconnect();
      desktop.removeEventListener('change', observeScrollArea);
    };
  }, [error, hasMore, isLoading, onLoadMore]);
  return (
    <div
      className="mt-3 grid min-h-20 place-items-center text-center text-sm text-ink-muted"
      ref={sentinel}
    >
      {error ? (
        <div>
          <p className="m-0 text-warning">The next {contentNoun} could not be loaded.</p>
          <button
            className={`${buttonBase} mt-3 border-line/55 bg-transparent text-ink-soft enabled:hover:border-brass/60 enabled:hover:bg-brass/10 enabled:hover:text-ink`}
            onClick={onLoadMore}
            type="button"
          >
            Try again
          </button>
        </div>
      ) : isLoading ? (
        <p aria-live="polite" className="m-0">
          Loading more {contentNoun}…
        </p>
      ) : hasMore ? (
        <p className="m-0">More {contentNoun} load as you continue.</p>
      ) : (
        <p className="m-0">All pending {contentNoun} are loaded.</p>
      )}
    </div>
  );
}

function ReviewCard({
  busyDecision,
  error,
  moderatedDecision,
  onDecision,
  review,
}: {
  busyDecision: BusyDecision | null;
  error: string | null;
  moderatedDecision: ReviewDecision | null;
  onDecision(
    review: PendingReview,
    decision: ReviewDecision,
    moderatorMessage?: string | null,
  ): void;
  review: PendingReview;
}) {
  const hasReviewerName = Boolean(review.reviewerName);
  const isBusy = busyDecision?.reviewId === review.id;
  const titleId = `review-title-${review.id}`;
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [rejectionValidationError, setRejectionValidationError] = useState<string | null>(null);

  function submitRejection() {
    const validationError = moderatorMessageValidationError(rejectionMessage);
    if (validationError) {
      setRejectionValidationError(validationError);
      return;
    }
    setRejectionValidationError(null);
    onDecision(review, 'rejected', normalizeModeratorMessage(rejectionMessage));
  }
  return (
    <article
      aria-labelledby={titleId}
      className="mb-5 inline-flex w-full min-w-0 break-inside-avoid flex-col overflow-hidden rounded-[17px] border border-line/75 bg-panel align-top shadow-panel lg:mb-6"
      data-moderation-state={moderatedDecision ?? 'pending'}
      data-review-id={review.id}
    >
      <div className="border-b border-line/30 p-5 sm:p-6">
        <div className="flex min-w-0 items-start gap-4">
          <div className="grid h-[114px] w-[76px] shrink-0 place-items-center overflow-hidden rounded-[12px] border border-line/75 bg-canvas-deep text-center text-[9px] leading-3 text-ink-muted shadow-sm">
            {review.book.coverImageUrl ? (
              <img
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                src={review.book.coverImageUrl}
              />
            ) : (
              <span aria-hidden="true">No cover</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`${eyebrow} mb-2`}>Public review</p>
            <h2
              className="m-0 font-serif text-[clamp(1.3rem,2.5vw,1.65rem)] font-medium leading-[1.12] tracking-[-0.02em] text-balance"
              id={titleId}
            >
              {review.book.title}
            </h2>
            <p className="mb-0 mt-2 line-clamp-2 text-sm leading-5 text-ink-muted">
              {review.book.authors.join(', ')}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              <BookFact
                label={review.book.isbn13 ? 'ISBN-13' : 'ISBN-10'}
                monospaced
                value={review.book.isbn13 ?? review.book.isbn10 ?? 'Not available'}
                wide
              />
              <BookFact
                label="Published"
                value={
                  [review.book.publisher, review.book.publishedYear].filter(Boolean).join(' · ') ||
                  'Not available'
                }
              />
              <BookFact
                label="Language"
                value={review.book.language?.toUpperCase() ?? 'Not available'}
              />
              <BookFact
                label="Length"
                value={
                  review.book.pageCount === null
                    ? 'Not available'
                    : `${review.book.pageCount} pages`
                }
              />
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-1.5 border-t border-line/25 pt-4 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4">
          <p
            className={`m-0 min-w-0 truncate font-semibold ${hasReviewerName ? 'text-ink-soft' : 'text-warning'}`}
          >
            {review.reviewerName ?? 'Reviewer name missing'}
          </p>
          <time className="text-ink-muted sm:text-right" dateTime={review.submittedAt}>
            {new Intl.DateTimeFormat(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(new Date(review.submittedAt))}
          </time>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <blockquote
          aria-label={`Review text for ${review.book.title}`}
          className="review-paper m-0 [overflow-wrap:anywhere] whitespace-pre-wrap px-6 pb-7 pt-7 font-serif text-[1.0625rem] leading-[1.7] text-paper-text text-pretty sm:px-7 sm:pb-8 sm:pt-8"
        >
          <p className="relative z-[3] m-0">{review.body}</p>
        </blockquote>
        <AutomatedContentCheck check={review.contentCheck} />
        <div className="mt-5 flex flex-wrap gap-2">
          {review.rating !== null ? (
            <span className="rounded-[5px] border border-thread/20 bg-thread-soft px-[9px] py-1.5 text-[11px] font-semibold tracking-[0.03em] text-thread">
              Rating {review.rating} / 5
            </span>
          ) : null}
          <span className="rounded-[5px] border border-thread/20 bg-thread-soft px-[9px] py-1.5 text-[11px] font-semibold tracking-[0.03em] text-thread">
            Source: {review.source}
          </span>
          {review.containsSpoilers ? (
            <span className="rounded-[5px] border border-danger/40 bg-danger-fill/50 px-[9px] py-1.5 text-[11px] font-semibold tracking-[0.03em] text-warning">
              Contains spoilers
            </span>
          ) : null}
        </div>
        {error ? <p className={inlineError}>{error}</p> : null}
        {!hasReviewerName ? (
          <p className={inlineError}>
            Approval is blocked until this reader completes their public profile in Belore. You may
            still reject the review.
          </p>
        ) : null}
      </div>
      <div className="border-t border-line/30 bg-canvas-deep/30 px-5 py-5 sm:px-6">
        {moderatedDecision ? (
          <div
            aria-live="polite"
            className={`grid min-h-11 place-items-center rounded-[12px] border px-4 py-2.5 text-center text-sm font-bold ${
              moderatedDecision === 'approved'
                ? 'border-safe/35 bg-safe/10 text-safe'
                : 'border-danger/40 bg-danger-fill/30 text-warning'
            }`}
          >
            {moderatedDecision === 'approved'
              ? 'Approved · kept here until reload'
              : 'Declined · kept here until reload'}
          </div>
        ) : rejectionOpen ? (
          <RejectionComposer
            busy={busyDecision !== null}
            error={rejectionValidationError}
            message={rejectionMessage}
            onCancel={() => {
              setRejectionOpen(false);
              setRejectionValidationError(null);
            }}
            onChange={(value) => {
              setRejectionMessage(value);
              setRejectionValidationError(null);
            }}
            onSubmit={submitRejection}
            reviewId={review.id}
            reviewTitle={review.book.title}
            submitting={isBusy && busyDecision.decision === 'rejected'}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            <button
              aria-label={`Decline review for ${review.book.title}`}
              className={`${buttonBase} border-danger/50 bg-danger-fill/20 text-warning enabled:hover:bg-danger-fill/45`}
              disabled={busyDecision !== null}
              onClick={() => setRejectionOpen(true)}
              type="button"
            >
              Decline
            </button>
            <button
              aria-label={`Approve review for ${review.book.title}`}
              className={primaryButton}
              disabled={busyDecision !== null || !hasReviewerName}
              onClick={() => onDecision(review, 'approved', null)}
              title={
                hasReviewerName ? undefined : 'A public reviewer name is required before approval.'
              }
              type="button"
            >
              {isBusy && busyDecision.decision === 'approved' ? 'Approving…' : 'Approve'}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export function RejectionComposer({
  busy,
  contentType = 'review',
  error,
  message,
  onCancel,
  onChange,
  onSubmit,
  reviewId,
  reviewTitle,
  submitting,
}: {
  busy: boolean;
  contentType?: 'quote' | 'review';
  error: string | null;
  message: string;
  onCancel(): void;
  onChange(value: string): void;
  onSubmit(): void;
  reviewId: string;
  reviewTitle: string;
  submitting: boolean;
}) {
  const messageLength = Array.from(message).length;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label className="block text-sm font-bold text-ink" htmlFor={`rejection-message-${reviewId}`}>
        What should the reader change?
      </label>
      <p className="mb-3 mt-1 text-xs leading-5 text-ink-muted">
        {contentType === 'review'
          ? 'This message will be shown privately to the reader with their review.'
          : 'This message is stored with the quote moderation event for the support team.'}
      </p>
      <textarea
        aria-describedby={`rejection-guidance-${reviewId}`}
        className={`min-h-28 w-full resize-y rounded-[12px] border border-control-line bg-panel-raised px-3.5 py-3 text-sm leading-6 text-ink placeholder:text-ink-muted disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
        disabled={busy}
        id={`rejection-message-${reviewId}`}
        maxLength={maxModeratorMessageLength}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={`Explain the issue and how the ${contentType} can be resubmitted.`}
        required
        value={message}
      />
      <div
        className="mt-1.5 flex items-start justify-between gap-4 text-xs"
        id={`rejection-guidance-${reviewId}`}
      >
        <span className={error ? 'text-warning' : 'text-ink-muted'}>
          {error ?? 'A specific, actionable explanation is required.'}
        </span>
        <span className="shrink-0 tabular-nums text-ink-muted">
          {messageLength} / {maxModeratorMessageLength}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        <button
          className={`${buttonBase} border-line/55 bg-transparent text-ink-soft enabled:hover:border-brass/60 enabled:hover:bg-brass/10 enabled:hover:text-ink`}
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
        <button
          aria-label={`Decline ${contentType} for ${reviewTitle}`}
          className={`${buttonBase} border-danger/50 bg-danger-fill/35 text-warning enabled:hover:bg-danger-fill/55`}
          disabled={busy || moderatorMessageValidationError(message, contentType) !== null}
          type="submit"
        >
          {submitting ? 'Declining…' : `Decline ${contentType}`}
        </button>
      </div>
    </form>
  );
}

export function normalizeModeratorMessage(value: string) {
  return value
    .normalize('NFC')
    .replace(/\r\n?/gu, '\n')
    .replace(/[\p{Cc}\p{Cf}]/gu, (character) =>
      character === '\n' || character === '\t' ? character : '',
    )
    .trim();
}

export function moderatorMessageValidationError(
  value: string,
  contentType: 'quote' | 'review' = 'review',
) {
  const length = Array.from(normalizeModeratorMessage(value)).length;
  if (length === 0) {
    return `Explain why this ${contentType} needs changes.`;
  }
  if (length > maxModeratorMessageLength) {
    return `Keep the message within ${maxModeratorMessageLength} characters.`;
  }
  return null;
}

function BookFact({
  label,
  monospaced = false,
  value,
  wide = false,
}: {
  label: string;
  monospaced?: boolean;
  value: string;
  wide?: boolean;
}) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`}>
      <p className="mb-1 mt-0 text-[9px] font-bold uppercase tracking-[0.12em] text-ink-muted">
        {label}
      </p>
      <p
        className={`m-0 text-[11px] leading-4 text-ink-soft ${wide ? 'break-words' : 'truncate'} ${monospaced ? 'font-mono tabular-nums' : ''}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

const contentSignalLabels: Record<ReviewContentSignalCode, string> = {
  contact_solicitation: 'Contact solicitation detected',
  credential_solicitation: 'Credential request near an external link',
  external_link: 'External link detected',
  obfuscated_text: 'Hidden or direction-changing text detected',
  promotional_language: 'Promotional language detected',
  unsafe_markup: 'Executable-looking markup detected',
};

export function AutomatedContentCheck({
  check,
  contentType = 'review',
}: {
  check: ReviewContentCheck;
  contentType?: 'quote' | 'review';
}) {
  if (check.status === 'unavailable') {
    return (
      <section
        aria-label="Automated content check"
        className="mt-5 rounded-[8px] border border-line/45 bg-canvas-deep/35 px-3.5 py-3 text-ink-muted"
      >
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden="true"
            className="mt-[5px] size-2.5 shrink-0 rounded-full bg-ink-muted"
          />
          <div className="min-w-0">
            <p className="m-0 text-xs font-bold uppercase tracking-[0.08em] text-ink-soft">
              Automated check unavailable
            </p>
            <p className="mb-0 mt-1 text-xs leading-5">
              This {contentType} came from an older API response. Check its content manually until
              screening data is available.
            </p>
          </div>
        </div>
      </section>
    );
  }
  const clear = check.status === 'clear';
  return (
    <section
      aria-label="Automated content check"
      className={`mt-5 rounded-[8px] border px-3.5 py-3 ${
        clear
          ? 'border-safe/35 bg-safe-fill/55 text-safe'
          : 'border-warning/45 bg-danger-fill/35 text-warning'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className={`mt-[5px] size-2.5 shrink-0 rounded-full ${clear ? 'bg-safe' : 'bg-warning'}`}
        />
        <div className="min-w-0">
          <p className="m-0 text-xs font-bold uppercase tracking-[0.08em]">
            {clear ? 'Automated check clear' : 'Automated check: review needed'}
          </p>
          {clear ? (
            <p className="mb-0 mt-1 text-xs leading-5 text-safe">
              No common advertising or malicious-content signals were found.
            </p>
          ) : (
            <ul className="mb-0 mt-1.5 grid gap-1 pl-4 text-xs leading-5">
              {check.signals.map((signal) => (
                <li key={`${signal.category}-${signal.code}`}>
                  {contentSignalLabels[signal.code]}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function PageHeading({
  contentNoun,
  handledCount,
  loadedCount,
  total,
  visibleCount,
}: {
  contentNoun: 'quotes' | 'reviews';
  handledCount: number;
  loadedCount: number;
  total: number;
  visibleCount: number;
}) {
  const waitingCount = Math.max(0, total - handledCount);
  const hasActiveFilter = visibleCount !== loadedCount;
  return (
    <div className="mb-7 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
      <div>
        <p className={eyebrow}>Moderation</p>
        <h1 className={headingOne}>Pending {contentNoun}</h1>
      </div>
      <div className="rounded-full border border-line bg-panel px-4 py-2 text-xs tabular-nums text-ink-soft">
        {loadedCount < total
          ? `${loadedCount} of ${total} loaded`
          : `${waitingCount} ${waitingCount === 1 ? contentNoun.slice(0, -1) : contentNoun} waiting`}
        {hasActiveFilter ? ` · ${visibleCount} match` : ''}
        {handledCount > 0 ? ` · ${handledCount} handled` : ''}
      </div>
    </div>
  );
}

function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="admin-auth grid min-h-dvh place-items-center p-[clamp(1.5rem,5vw,4rem)]">
      {children}
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg className="block size-[21px]" aria-hidden="true" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.6h3.3c1.9-1.8 2.9-4.4 2.9-7.5Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 5-.9 6.7-2.3l-3.3-2.6c-.9.6-2.1 1-3.4 1a5.9 5.9 0 0 1-5.5-4.1H3.1v2.6A10.1 10.1 0 0 0 12 22Z"
      />
      <path fill="#FBBC05" d="M6.5 14a6 6 0 0 1 0-3.9V7.4H3.1a10.1 10.1 0 0 0 0 9.2L6.5 14Z" />
      <path
        fill="#EA4335"
        d="M12 6c1.5 0 2.8.5 3.9 1.5l2.9-2.8A9.8 9.8 0 0 0 3.1 7.4l3.4 2.7A5.9 5.9 0 0 1 12 6Z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="block size-[21px]" aria-hidden="true" viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M16.8 12.7c0-2.6 2.2-3.9 2.3-4a5 5 0 0 0-4-2.2c-1.7-.2-3.3 1-4.2 1-.9 0-2.2-1-3.7-1a5.2 5.2 0 0 0-4.4 2.7c-1.9 3.2-.5 8 1.3 10.6.9 1.3 2 2.8 3.4 2.7 1.4 0 1.9-.9 3.6-.9s2.2.9 3.6.9c1.5 0 2.4-1.3 3.3-2.6a11.5 11.5 0 0 0 1.5-3.1 4.6 4.6 0 0 1-2.7-4.1ZM14 4.7a4.7 4.7 0 0 0 1.1-3.3 4.7 4.7 0 0 0-3.1 1.6 4.4 4.4 0 0 0-1.2 3.2A4 4 0 0 0 14 4.7Z"
      />
    </svg>
  );
}

function CenteredState({
  action,
  children,
  onAction,
  title,
}: {
  action?: string;
  children?: React.ReactNode;
  onAction?: () => void;
  title: string;
}) {
  return (
    <section className={stateCard} aria-live="polite">
      <img
        className="mb-[26px] size-[42px] object-contain"
        src="/assets/belore-mark-dark.svg"
        alt=""
        aria-hidden="true"
      />
      <h1 className={`${headingOne} mb-3.5`}>{title}</h1>
      {children ? (
        <p className="mb-6 mt-0 max-w-[58ch] leading-[1.65] text-ink-soft text-pretty last:mb-0">
          {children}
        </p>
      ) : null}
      {action && onAction ? (
        <button className={primaryButton} onClick={onAction} type="button">
          {action}
        </button>
      ) : null}
    </section>
  );
}

function messageForError(cause: unknown) {
  if (cause instanceof AdminApiError) {
    switch (cause.code) {
      case 'ADMIN_QUOTE_CHANGED':
        return 'This quote changed in another session. Reload the queue and review it again.';
      case 'ADMIN_REVIEW_CHANGED':
        return 'This review changed in another session. Reload the queue and review it again.';
      case 'ADMIN_REVIEWER_NAME_REQUIRED':
        return 'This review cannot be approved until its author has a public profile name.';
      case 'ADMIN_REJECTION_MESSAGE_REQUIRED':
      case 'ADMIN_REJECTION_MESSAGE_INVALID':
        return 'Add a specific explanation of what the reader should change before declining.';
      case 'ADMIN_RESPONSE_INVALID':
        return 'The API returned an unexpected response. No moderation decision was applied by this screen.';
      default:
        return 'The administration API could not complete the request.';
    }
  }
  return cause instanceof Error
    ? cause.message
    : 'The administration API could not complete the request.';
}
